import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface PortalModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose?: () => void;
}

const PortalModal: React.FC<PortalModalProps> = ({ children, isOpen, onClose }) => {
  // Handle escape key and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Add escape key listener
    document.addEventListener('keydown', handleEscape);

    // Focus management - focus the modal when it opens
    const modalElement = document.querySelector('[data-portal-modal]') as HTMLElement;
    if (modalElement) {
      modalElement.focus();
    }

    return () => {
      // Restore body scroll
      document.body.style.overflow = originalOverflow;
      
      // Remove escape key listener
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      data-portal-modal
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        outline: 'none'
      }}
      tabIndex={-1}
    >
      {children}
    </div>,
    document.body
  );
};

export default PortalModal;
